import { Fragment, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Paragraph1, Paragraph2, Divider, SmallText, Button } from 'inpock-ui-react';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import {
  api,
  DetailedOrderLine,
  OrderlineStatus,
  Orders,
  OrdersDetailResponse,
  OrderStatus,
} from '@/apis';
import { Callout, Tooltip } from '@/components/common';
import { DELIVERY_COMPANY, ORDERLINE_STATUS, REVIEW_SESSION_KEY } from '@/constants';
import { copy, formatPayType, generateOrderlineName, openPopup, ORDER_TEMPLATE } from '@/utils';
import { useDialog, useToast } from '@/hooks';
import { keys } from '@/hooks/queries/keys';
import { urlFormatter } from '@/utils/urlFormatter';

import { RefundModal } from '../RefundModal';
import { OrderInfoLine } from '../../checkout/OrderInfoLine';
import { OrderItem } from '../OrderItem';

import * as S from './styled';

interface OrderInfoSectionProps {
  type: 'campaign' | 'payment' | 'delivery' | 'buyer';
  actionTitle?: React.ReactNode;
  children?: React.ReactNode;
  order?: OrdersDetailResponse;
  isMember?: boolean;
  phone?: number;
}

const ORDER_INFO_TITLE: Record<string, string> = {
  campaign: '상품 정보',
  payment: '결제 정보',
  delivery: '배송 정보',
  buyer: '구매자 정보',
} as const;

const redirectToValidURL = (url: string) => {
  // 정규 표현식을 사용하여 URL이 "http://" 또는 "https://"로 시작하는지 확인
  const urlPattern = /^https?:\/\//i;

  if (!urlPattern.test(url)) {
    // URL에 "http://"를 추가하여 새 URL을 생성
    const newURL = 'http://' + url;
    // 현재 페이지를 새 URL로 리다이렉트
    window.location.href = newURL;
  } else {
    window.location.href = url;
  }
};

function ReviewButton(orderline: DetailedOrderLine) {
  const params = useParams();
  const { id: order_group_number } = params;
  const navigate = useNavigate();

  const onClickReviewButton = (type: 'register' | 'modify') => {
    if (type === 'register') {
      sessionStorage.setItem(
        REVIEW_SESSION_KEY,
        JSON.stringify({
          orderline,
          order_group_number,
        }),
      );
      // 리뷰 작성 시에는 orderline 정보를 state로 넘겨야 함
      return navigate(`/customer/orders/review/${orderline.orderline_number}/register`);
    }

    sessionStorage.setItem(
      REVIEW_SESSION_KEY,
      JSON.stringify({
        order_group_number,
      }),
    );
    navigate(`/customer/orders/review/${orderline.review.id}/modify`);
  };

  return (
    <Button
      fullWidth
      size="small"
      type={orderline.review ? 'line' : 'primary'}
      onClick={() => {
        onClickReviewButton(orderline.review ? 'modify' : 'register');
      }}
    >
      {orderline.review ? '작성한 리뷰 확인' : '리뷰작성'}
    </Button>
  );
}

// 상품 정보 영역
function CampaignInfo({ order, children, isMember, phone }: Partial<OrderInfoSectionProps>) {
  const queryClient = useQueryClient();
  const [dialogOn, dialogOff] = useDialog();
  const toastOn = useToast();
  const [isShowingRefundModal, setIsShowingRefundModal] = useState<boolean>(false);
  const [refundLoading, setRefundLoading] = useState<boolean>(false);
  const [cancelOrders, setCancelOrders] = useState<{
    order_number: string;
    orderlines: DetailedOrderLine[];
  } | null>(null);

  const [showInfo, setShowInfo] = useState({
    idx: -1,
    show: false,
  });

  const onClickInfo = (idx: number) => {
    setShowInfo({
      idx,
      show: !showInfo.show,
    });
  };

  const onClickCSChannel = () => {
    if (order?.cs_channel) {
      const phonePattern = /^\d{10,14}$/;

      if (phonePattern.test(order?.cs_channel)) {
        return dialogOn({
          template: ORDER_TEMPLATE.campaign.cs(),
          confirm: {
            type: 'primary',
            text: '바로 연락하기',
            onClick: () => {
              dialogOff();
              const phoneLink = `tel:${order.cs_channel}`;
              return (window.location.href = phoneLink);
            },
          },
        });
      }

      redirectToValidURL(order?.cs_channel as string);
    } else {
      toastOn('문의 채널이 등록되어있지 않아요 😢');
    }
  };

  const onClickInquiry = (cs_channel: string) => {
    if (cs_channel.match(/^[0-9-+]+$/)) {
      return window.open(`tel:${cs_channel}`, '_blank');
    }

    return window.open(urlFormatter(cs_channel), '_blank');
  };

  // 주문 취소 체크
  const checkCancelPossible = (order: Orders) => {
    const { order_number, orderlines } = order;
    // 주문 전체가 입금대기중이거나
    const isOrderUnpaid = orderlines.every(
      (orderline) => orderline.status === OrderlineStatus.UNPAID,
    );

    // 주문 전체가 상품 준비중, 배송중, 배송완료면 취소 불가
    const isCancelImpossible = orderlines.every((orderline) =>
      [OrderlineStatus.PREPARING, OrderlineStatus.IN_TRANSIT, OrderlineStatus.DELIVERED].includes(
        orderline.status,
      ),
    );

    if (isOrderUnpaid) {
      return dialogOn({
        template: ORDER_TEMPLATE.cancel.unpaid(),
      });
    }

    if (isCancelImpossible) {
      return dialogOn({
        template: ORDER_TEMPLATE.cancel.onlyPaid(),
        confirm: {
          text: '문의하기',
          type: 'primary',
          onClick: () => {
            onClickCSChannel();
            dialogOff();
          },
        },
      });
    }

    setCancelOrders({
      order_number,
      orderlines: orderlines.filter((orderline) => orderline.status === OrderlineStatus.PAID),
    });

    return true;
  };

  const onClickCancelOrder = (orders: Orders) => {
    if (checkCancelPossible(orders)) {
      setIsShowingRefundModal(true);
    }
  };

  const 주문취소 = async (
    cancel_data: { orderline_number: string; quantity: number }[],
    cancel_amount: number,
    order_number: string,
  ) => {
    setRefundLoading(true);
    const payload = {
      isMember: Boolean(isMember),
      order_number,
      cancel_data,
      cancel_amount,
      ...(!isMember && { phone }),
    };
    try {
      const { status } = await api.orders.cancelOrder(payload);
      if (status === 204) {
        queryClient.refetchQueries(keys.orders.all);
        toastOn('주문 취소 성공 😎');
        dialogOff();
      }
    } catch (error) {
      if (isAxiosError(error)) {
        const { response } = error;
        const { message } = response?.data;
        if (message) {
          return dialogOn({
            template: ORDER_TEMPLATE.cancel.exception(message),
            confirm: {
              text: '문의하기',
              type: 'primary',
              onClick: () => {
                openPopup('https://inpocklink.channel.io/home', 375, 812);
                dialogOff();
              },
            },
          });
        }
      }
      dialogOn({
        template: ORDER_TEMPLATE.cancel.exception(),
        confirm: {
          text: '문의하기',
          type: 'primary',
          onClick: () => {
            openPopup('https://inpocklink.channel.io/home', 375, 812);
            dialogOff();
          },
        },
      });
    } finally {
      setRefundLoading(false);
      setIsShowingRefundModal(false);
    }
  };

  const onClickCopyInvoice = (invoice_number: string) => {
    copy(invoice_number);
    toastOn('송장번호가 복사되었어요 😎');
  };

  if (!order) return <></>;

  return (
    <>
      {order.orders.map((orderGroup, index) => {
        return (
          <Fragment key={index}>
            {isShowingRefundModal &&
              order &&
              orderGroup.order_number === cancelOrders?.order_number && (
                <RefundModal
                  externalShow={isShowingRefundModal}
                  order_number={cancelOrders?.order_number ?? ''}
                  orderlines={cancelOrders?.orderlines ?? []}
                  paymentInfo={order?.payment_info}
                  onCloseModal={() => {
                    setIsShowingRefundModal(false);
                    setCancelOrders(null);
                  }}
                  onClickCancel={(cancel_data, cancel_amount) =>
                    주문취소(cancel_data, cancel_amount, orderGroup.order_number)
                  }
                  loading={refundLoading}
                  csChannel={order.cs_channel}
                />
              )}
            <S.OrderGroupContainer>
              <div>
                <div className="order-group-header">
                  <Paragraph2 className="text-bold">배송묶음</Paragraph2>
                  <Paragraph2 className="text-gray-600">
                    주문번호 {orderGroup.order_number}
                  </Paragraph2>
                </div>
                {orderGroup.orderlines.map((orderline, index) => (
                  <Fragment key={orderline.orderline_id}>
                    <OrderItem
                      image={orderline.campaign_image[0].image}
                      campaignName={orderline.campaign_name}
                      orderlineName={generateOrderlineName(
                        orderline.additional_product_name,
                        orderline.additional_option_name,
                        orderline.main_option_name,
                        orderline.sub_option_name,
                        orderline.quantity,
                      )}
                      price={orderline.unit_price * orderline.quantity}
                      header={
                        <S.OrderItemHeader>
                          <S.StatusBadge>
                            <SmallText color="orange" style={{ fontWeight: '700' }}>
                              {ORDERLINE_STATUS[orderline.status as OrderlineStatus]}
                            </SmallText>
                          </S.StatusBadge>
                          {orderline.invoice && (
                            <div className="d-flex" style={{ gap: '4px' }}>
                              <SmallText className="text-gray-500">
                                {
                                  DELIVERY_COMPANY[
                                    orderline.invoice.company as keyof typeof DELIVERY_COMPANY
                                  ]
                                }{' '}
                                {orderline.invoice.invoice_number}
                              </SmallText>
                              {orderline.invoice.invoice_number && (
                                <S.ButtonCopy
                                  onClick={() =>
                                    orderline.invoice &&
                                    onClickCopyInvoice(orderline.invoice.invoice_number)
                                  }
                                />
                              )}
                              {orderline.invoice.company === 'DIRECT' && (
                                <Tooltip
                                  tooltipId="direct-delivery-hint"
                                  place="top-start"
                                  openOnClick
                                >
                                  <SmallText className="text-white">
                                    판매자가 직접배송 처리하였습니다.
                                    <br />
                                    자세한 사항은 판매자에게 문의해 주세요.
                                  </SmallText>
                                </Tooltip>
                              )}
                            </div>
                          )}
                        </S.OrderItemHeader>
                      }
                      footer={
                        <>
                          <div
                            style={{
                              width: '100%',
                              gap: '8px',
                            }}
                            className="d-flex align-items-center"
                          >
                            <Button
                              fullWidth
                              type="line"
                              size="small"
                              onClick={() => onClickInquiry(orderline.cs_channel)}
                            >
                              문의하기
                            </Button>
                            {/* 결제완료인 경우에만 취소 버튼 노출 */}
                            {orderline.status === OrderlineStatus.PAID && (
                              <Button
                                fullWidth
                                type="line"
                                size="small"
                                onClick={() => onClickCancelOrder(orderGroup)}
                              >
                                주문취소
                              </Button>
                            )}
                            {/* 회원이면서 리뷰가 있거나, 리뷰가 없는데 배송완료 상태면 리뷰 버튼 노출  */}
                            {isMember &&
                              (orderline.review ||
                                (!orderline.review &&
                                  orderline.status === OrderlineStatus.DELIVERED)) && (
                                <>{ReviewButton(orderline)}</>
                              )}
                          </div>
                          {/* 취소 완료, 취소 대기 주문의 경우 취소 정보 노출 */}
                          {[OrderlineStatus.CANCELED, OrderlineStatus.CANCEL_WAITING].includes(
                            orderline.status as OrderlineStatus,
                          ) && (
                            <S.OrderItemFooter>
                              {orderline.canceled_at && (
                                <div className="order-line-footer-info">
                                  <Paragraph2 color="gray500">취소일시</Paragraph2>
                                  <Paragraph2 style={{ fontWeight: '700' }}>
                                    {dayjs(orderline.canceled_at).format('YYYY. MM. DD HH:mm')}
                                  </Paragraph2>
                                </div>
                              )}
                              {orderline.cancel_reason && (
                                <div className="order-line-footer-info">
                                  <Paragraph2 color="gray500">취소사유</Paragraph2>
                                  <Paragraph2 style={{ fontWeight: '700' }}>
                                    {orderline.cancel_reason}
                                  </Paragraph2>
                                </div>
                              )}
                            </S.OrderItemFooter>
                          )}
                        </>
                      }
                    />
                    {index !== orderGroup.orderlines.length - 1 && <Divider spacing={20} />}
                  </Fragment>
                ))}
              </div>
              <Divider spacing={20} />
              <S.DeliveryFeeContainer>
                <Paragraph2>
                  상품{' '}
                  {order.orders[index].total_price
                    ? order.orders[index].product_price.toLocaleString()
                    : 0}{' '}
                  + 배송비{' '}
                  {order.orders[index].total_price
                    ? order.orders[index].shipping_fee.toLocaleString()
                    : 0}{' '}
                  ={' '}
                  <span style={{ fontWeight: '700' }}>
                    {order.orders[index].total_price.toLocaleString()}
                  </span>
                </Paragraph2>
                <Tooltip tooltipId="delivery-hint" place="top-start" openOnClick>
                  <SmallText className="text-white">
                    동일 배송정책이 적용된 상품은
                    <br />
                    묶음 배송으로 처리되며, <br />
                    배송비는 한 번만 부과됩니다.
                  </SmallText>
                </Tooltip>
              </S.DeliveryFeeContainer>
            </S.OrderGroupContainer>
          </Fragment>
        );
      })}
      {children && <>{children}</>}
    </>
  );
}

function Info({ order, type, children, isMember, phone }: Partial<OrderInfoSectionProps>) {
  if (!order) return <></>;

  if (type === 'buyer') {
    return (
      <>
        <OrderInfoLine label="주문자" content={`${order?.buyer_name ?? ''}`} />
        <OrderInfoLine label="전화번호" content={`${order?.buyer_phone ?? ''}`} />
        <OrderInfoLine label="이메일" content={`${order?.buyer_email ?? ''}`} />
        {children && <>{children}</>}
      </>
    );
  }

  if (type === 'delivery') {
    return (
      <>
        <OrderInfoLine label="수령인" content={`${order?.receiver_name ?? ''}`} />
        <OrderInfoLine label="수령인 전화번호" content={`${order?.receiver_phone ?? ''}`} />
        <OrderInfoLine
          label="배송지"
          content={`${order?.receiver_address ?? ''} ${order?.receiver_detail_address ?? ''}`}
          lineClamp={5}
        />
        <OrderInfoLine label="배송메모" content={`${order?.delivery_message ?? ''}`} />
        {children && <>{children}</>}
      </>
    );
  }

  if (type === 'payment') {
    return (
      <>
        <OrderInfoLine
          label="총 상품가격"
          content={`${(order?.product_price ?? '').toLocaleString()}원`}
        />
        <OrderInfoLine
          label="배송비"
          content={`${(order?.shipping_fee ?? '').toLocaleString()}원`}
        />
        {order?.discount_price ? (
          <OrderInfoLine
            label="상품 할인"
            content={`${(order?.discount_price ?? '').toLocaleString()}원`}
          />
        ) : null}
        {order?.payment_info.pay_type === 'cash' && (
          <OrderInfoLine
            label="주문일시"
            content={`${dayjs(order?.created_at ?? '').format('YYYY. MM. DD HH:mm')}`}
          />
        )}
        {!(
          order?.payment_info.pay_type === 'cash' &&
          order?.orders.some((order) => order.status === OrderStatus.UNPAID)
        ) &&
          order?.paid_at && (
            <OrderInfoLine
              label="결제일시"
              content={`${dayjs(order?.paid_at ?? '').format('YYYY. MM. DD HH:mm')}`}
            />
          )}
        <OrderInfoLine label="결제수단" content={formatPayType(order?.payment_info)} />
        {order?.payment_info.pay_type === 'cash' && (
          <>
            <OrderInfoLine
              label="현금영수증 발행여부"
              content={order?.cash_receipt_type ? '발행' : '미발행'}
            />
            <Callout
              text="✋ 현금영수증 미신청 시, 자진발급 처리되며, 결제일 다음 날 오전 10시 이후 국세청에서
                승인번호를 통해 현금영수증 번호를 변경할 수 있어요"
            />
          </>
        )}
        {order?.cash_receipt_url && <OrderInfoLine label="현금영수증 발행여부" content={'발행'} />}
        {children && <>{children}</>}
      </>
    );
  }

  return (
    <>
      <CampaignInfo order={order} isMember={isMember} phone={phone} />
      {children && <>{children}</>}
    </>
  );
}

export function OrderInfoSection({
  actionTitle,
  children,
  order,
  type,
  isMember,
  phone,
}: OrderInfoSectionProps) {
  return (
    <S.Container>
      <S.TitleContainer>
        <S.Title>
          <div className="d-flex align-items-center">
            <Paragraph1>{ORDER_INFO_TITLE[type]}</Paragraph1>
          </div>
        </S.Title>
        {actionTitle}
      </S.TitleContainer>
      <div className="order-info">
        {order && type && (
          <Info order={order} type={type} isMember={isMember} phone={phone}>
            {children && <>{children}</>}
          </Info>
        )}
      </div>
    </S.Container>
  );
}
